import { getDocs } from '../../payload/payload.helpers'
import { PayloadUser } from '../../payload/payload-types.zod'
import z from 'zod'

import {
  type EntitlementInput,
  IndividualDb,
  individualDbToResponse,
  IndividualResponseSchema,
} from './individuals'

export const UserDbSchema = PayloadUser

export const UserResponseSchema = UserDbSchema.omit({
  auth: true,
  // Admin-only: derived for the Payload title, nothing in the app reads it. Omitted
  // so adding the field doesn't change the OpenAPI shape and force a client regen.
  displayName: true,
  individuals: true,
}).extend({
  individuals: z.array(IndividualResponseSchema),
})

/**
 * What the signed-in user may change about themselves. Only the email so far — everything
 * else on a user comes from BankID and is not theirs to edit.
 *
 * The empty string is the clear signal, as it is for contacts: the generated client
 * flattens `anyOf: [string, null]` to `string | undefined`, so a null-only contract would
 * be untypeable on that side. The service maps '' to null when writing.
 */
export const UpdateMeSchema = z.object({
  email: z.union([z.email().max(200), z.literal('')]),
})

export const CreateUserSchema = UserDbSchema.pick({
  firstName: true,
  id: true,
  lastName: true,
  pid: true,
}).extend({ auth: z.string() })

export const CreateUserDbSchema = UserDbSchema.omit({
  createdAt: true,
  updatedAt: true,
})

export type UserDb = z.infer<typeof UserDbSchema>

export type UserResponse = z.infer<typeof UserResponseSchema>

export type CreateUser = z.infer<typeof CreateUserSchema>

export type UpdateMe = z.infer<typeof UpdateMeSchema>

export type CreateUserDb = z.infer<typeof CreateUserDbSchema>

/**
 * `entitlements` is keyed by individual id. Passed in for the same reason as in
 * `individualDbToResponse`: entitlement lives in its own collection, and a schema
 * function cannot go and fetch it. Omitting it leaves every individual grandfathered as
 * active, which is the fail-open default rather than a silent lockout.
 */
export const userDbToResponse = (
  data: UserDb,
  entitlements?: Map<string, EntitlementInput>,
) => {
  const individuals = getDocs<IndividualDb>(data.individuals)

  return UserResponseSchema.parse({
    createdAt: data.createdAt,
    email: data.email,
    firstName: data.firstName,
    id: data.id,
    individuals: individuals.map(individual =>
      individualDbToResponse(individual, entitlements?.get(individual.id)),
    ),
    lastName: data.lastName,
    pid: data.pid,
    updatedAt: data.updatedAt,
  } satisfies UserResponse)
}
